// PostHog telemetry wrapper.
//
// The posthog-js SDK (~190 KB) is loaded via dynamic import() so it never
// lands in the eager entry bundle and never blocks first paint. When
// telemetry is disabled the SDK is never fetched at all. All exported
// functions keep a synchronous void signature so callers don't change;
// calls made before the SDK finishes loading are queued and flushed once
// it's ready.
import type { PostHog } from "posthog-js";

const ENABLED = import.meta.env.VITE_POSTHOG_ENABLED === "true";
const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

let client: PostHog | null = null;
let loading = false;
let initStarted = false;

/** Calls queued while the SDK is still loading, replayed in order once ready. */
const pendingQueue: Array<(ph: PostHog) => void> = [];

function enqueue(fn: (ph: PostHog) => void): void {
  if (client) {
    fn(client);
    return;
  }
  // Drop silently if telemetry isn't initializing — nothing will ever flush it.
  if (!initStarted) return;
  pendingQueue.push(fn);
}

function flushQueue(): void {
  if (!client) return;
  while (pendingQueue.length) {
    const fn = pendingQueue.shift();
    try {
      fn?.(client);
    } catch (err) {
      console.error("[posthog] queued call failed:", err);
    }
  }
}

export function initPosthog(): void {
  if (initStarted) return;
  if (!ENABLED || !KEY) {
    console.log(`[posthog] disabled (enabled=${ENABLED}, hasKey=${!!KEY})`);
    return;
  }
  initStarted = true;
  loading = true;
  // Deferred load — keeps posthog-js out of the critical-path bundle.
  import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(KEY, {
        api_host: HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        mask_all_text: true,
        person_profiles: "identified_only",
        capture_exceptions: true,
      });
      client = posthog;
      loading = false;
      console.log(`[posthog] enabled (host=${HOST})`);
      flushQueue();
    })
    .catch((err) => {
      loading = false;
      console.error("[posthog] failed to load:", err);
    });
}

export function identifyUser(
  userId: string,
  organizationId: string | undefined,
  role: string,
  analyticsEnabled: boolean
): void {
  enqueue((ph) => {
    if (!analyticsEnabled) {
      ph.opt_out_capturing();
      return;
    }
    ph.opt_in_capturing();
    ph.identify(userId, { role });
    if (organizationId) {
      ph.group("organization", organizationId);
    }
  });
}

export function resetUser(): void {
  enqueue((ph) => ph.reset());
}

export function captureEvent(
  event: string,
  properties?: Record<string, unknown>
): void {
  enqueue((ph) => {
    try {
      ph.capture(event, properties);
    } catch (err) {
      console.error("[posthog] capture failed:", err);
    }
  });
}

export function captureException(
  error: Error,
  properties?: Record<string, unknown>
): void {
  enqueue((ph) => {
    try {
      ph.capture("$exception", {
        ...properties,
        $exception_message: error.message,
        $exception_type: error.name,
        $exception_stack_trace_raw: error.stack,
      });
    } catch (err) {
      console.error("[posthog] captureException failed:", err);
    }
  });
}

export function isPosthogEnabled(): boolean {
  return client !== null || loading;
}
